import { useCallback, useEffect, useRef, useState } from "react";
import * as Tone from "tone";
import { getPiano, kickAudio, onAudioStop, stopAllAudio } from "@/audio/piano";
import type { Hand, Score, ScoreNote } from "./types";

/**
 * Playback for an engraved score.
 *
 * The discipline here is lifted from the Rhythm Arithmetic engine, because that
 * is the part of it that actually feels good:
 *
 *  1. Every note is SCHEDULED AHEAD against the audio clock. Nothing is fired by
 *     a timer. Timers drift under load and in background tabs; the audio clock
 *     does not.
 *  2. The moving highlight is derived from that same audio clock on every frame,
 *     rather than being pushed by its own timer. Sound and sight are then the
 *     same number read twice, so they cannot separate.
 *  3. Playback always loops, because practice is looping.
 *  4. Tempo is a multiplier applied at schedule time, so 60% speed is the whole
 *     texture slowed, not a different arrangement.
 *
 * Everything is driven off `score.notes`, whose `start` and `dur` are already in
 * quarter notes — the same data the renderer engraves.
 */

export interface PlaybackOptions {
  /** Beats per minute for a quarter note. Defaults to the score's own tempo. */
  bpm?: number;
  loop?: boolean;
  /** Inclusive bar range to play. Omit for the whole piece. */
  barRange?: [number, number];
  /** Which hand to sound. "both" plays the grand staff as written. */
  hand?: Hand | "both";
  /** Click on every beat. */
  metronome?: boolean;
  /** Sound one empty bar before the first note. */
  countIn?: boolean;
}

export interface PlaybackState {
  playing: boolean;
  /** Index into score.notes of the note sounding now, or null. */
  playingIndex: number | null;
  /** Beats elapsed from the start of the played range. */
  beat: number;
  loading: boolean;
}

const LOOKAHEAD = 0.12;

export function useScorePlayback(score: Score, opts: PlaybackOptions = {}) {
  const {
    bpm, loop = true, barRange, hand = "both",
    metronome = false, countIn = true,
  } = opts;

  const [state, setState] = useState<PlaybackState>({
    playing: false, playingIndex: null, beat: 0, loading: false,
  });

  const rafRef = useRef(0);
  const clockRef = useRef<{ startAt: number; secsPerBeat: number; span: number } | null>(null);
  const stopRef = useRef<(() => void) | null>(null);
  /**
   * Where the playhead is, in quarter notes, updated every animation frame.
   *
   * This is deliberately a ref and not state. Writing the beat into React
   * state re-rendered the lesson and the whole engraved score sixty times a
   * second, which is what made the playhead stutter and lag behind the sound.
   * The score element is static while a piece plays — only the playhead moves —
   * so the view reads this ref in its own frame loop and moves one element.
   */
  const beatRef = useRef<number | null>(null);

  // Live values the render loop reads. Kept in a ref so changing the tempo
  // mid-loop takes effect on the next cycle without re-subscribing anything.
  const live = useRef({ bpm, loop, barRange, hand, metronome, countIn });
  live.current = { bpm, loop, barRange, hand, metronome, countIn };

  /**
   * The notes to sound, with TIES ALREADY FOLDED IN.
   *
   * A tie means one sound held, not two sounds in a row. Playing the written
   * note values straight re-attacks the piano on the far side of every tie —
   * audible on thirteen of the thirty pieces, and flatly wrong on the ones whose
   * whole point is the held note (Jesu Joy's closing minims, the last bar of
   * Twinkle). The continuation keeps the head note's index, so the highlight
   * also stays put for the length of the held sound instead of jumping.
   */
  const notes = useCallback(() => {
    const [lo, hi] = barRange ?? [-Infinity, Infinity];
    const out: { n: ScoreNote; i: number; dur: number }[] = [];
    /** Ties left open, keyed by hand — the same book-keeping the renderer does. */
    const open = new Map<string, { n: ScoreNote; i: number; dur: number }>();

    score.notes.forEach((n, i) => {
      if (n.bar < lo || n.bar > hi) return;
      if (hand !== "both" && n.hand !== hand) return;
      if (n.rest) return;

      const key = `${n.hand}:${n.midi ?? n.pitch}`;
      if (n.tie === "stop" || n.tie === "both") {
        const held = open.get(key);
        if (held) {
          // Extend the sound that is already ringing rather than striking again.
          held.dur = n.start + n.dur - held.n.start;
          if (n.tie === "both") open.set(key, held);
          else open.delete(key);
          return;
        }
      }
      const ev = { n, i, dur: n.dur };
      out.push(ev);
      if (n.tie === "start" || n.tie === "both") open.set(key, ev);
    });
    return out;
  }, [score, barRange, hand]);

  const stop = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    clockRef.current = null;
    stopRef.current?.();
    stopRef.current = null;
    beatRef.current = null;
    stopAllAudio();
    setState((s) => ({ ...s, playing: false, playingIndex: null, beat: 0 }));
  }, []);

  // Another component starting a sound silences ours; reflect that in the UI
  // instead of leaving a stale "playing" state and a frozen playhead.
  useEffect(() => onAudioStop(() => {
    cancelAnimationFrame(rafRef.current);
    clockRef.current = null;
    setState((s) => (s.playing ? { ...s, playing: false, playingIndex: null } : s));
  }), []);

  useEffect(() => () => {
    cancelAnimationFrame(rafRef.current);
    stopRef.current?.();
  }, []);

  const play = useCallback(async () => {
    stop();
    setState((s) => ({ ...s, loading: true }));
    kickAudio();
    let piano: Tone.Sampler;
    try {
      piano = await getPiano();
      await Tone.start();
    } catch {
      setState((s) => ({ ...s, loading: false }));
      return;
    }

    const picked = notes();
    if (!picked.length) {
      setState((s) => ({ ...s, loading: false }));
      return;
    }

    const tempo = live.current.bpm ?? score.tempo;
    const secsPerBeat = 60 / tempo;
    const barQ = score.barQuarters;
    // The PRINTED beat, in quarter notes: a crotchet in 4/4, a quaver in 6/8,
    // a minim in 2/2. Counting the count-in in crotchets regardless is what made
    // a 6/8 bar click six times over three beats' worth of lead — the last three
    // clicks landing on top of the music.
    const beatsPerBar = score.time[0];
    const beatQ = barQ / beatsPerBar;

    // Times are relative to the first note's bar, so a loop over bars 5-8 starts
    // at zero rather than at bar 5's absolute position.
    const firstBar = Math.min(...picked.map((p) => p.n.bar));
    const lastBar = Math.max(...picked.map((p) => p.n.bar));
    const origin = (firstBar - 1) * barQ;
    const span = (lastBar - firstBar + 1) * barQ;

    const ctx = Tone.getContext();
    const lead = countIn ? barQ * secsPerBeat : 0;
    let cycleStart = ctx.currentTime + LOOKAHEAD + lead;
    const t0 = cycleStart;

    let cancelled = false;
    const timers: number[] = [];
    // Clicks are scheduled straight onto the raw AudioContext, so the Tone
    // stop-bus cannot reach them: without tracking each oscillator, every
    // click already queued for the current cycle kept sounding after Stop.
    const clicks: OscillatorNode[] = [];
    stopRef.current = () => {
      cancelled = true;
      timers.forEach((t) => window.clearTimeout(t));
      clicks.forEach((o) => { try { o.stop(); o.disconnect(); } catch { /* already ended */ } });
      clicks.length = 0;
      try { piano.releaseAll(); } catch { /* already gone */ }
    };

    /**
     * Schedule only the next half-second of music, refilled on a timer.
     *
     * The whole piece used to be handed to the sampler in one go. Web Audio
     * honours those attacks whatever happens afterwards, so pressing Stop
     * silenced the metronome and left the piano playing to the end of the
     * piece — up to a minute of music the student could not stop. Cancelling
     * a timer cannot unschedule a note that is already queued; the only fix
     * is to never queue much.
     *
     * So: keep a pointer into the event list, push whatever falls inside the
     * window, and let a 120 ms ticker top it up. Stop clears the ticker, and
     * the most that can still be pending is one window.
     */
    const WINDOW = 0.5;      // seconds of music queued ahead of the clock
    const REFILL = 120;      // ms between top-ups

    let cursorEv = 0;        // next event index within the current cycle
    let cursorClick = 0;     // next metronome click within the current cycle
    const clicksPerCycle = Math.max(1, Math.round(span / beatQ));
    const cycleSecs = span * secsPerBeat;

    const pump = () => {
      if (cancelled) return;
      const until = ctx.currentTime + WINDOW;

      // Notes and clicks are pushed cycle by cycle; when a cycle is fully
      // queued we either roll into the next one (loop) or stop after it ends.
      while (!cancelled) {
        while (cursorEv < picked.length) {
          const ev = picked[cursorEv];
          const at = cycleStart + (ev.n.start - origin) * secsPerBeat;
          if (at > until) break;
          const dur = Math.max(0.08, ev.dur * secsPerBeat * 0.94);
          if (at >= ctx.currentTime - 0.02) {
            try {
              piano.triggerAttackRelease(ev.n.pitch as string, dur, at, 0.8);
            } catch { /* pitch out of sampler range: skip rather than abort */ }
          }
          cursorEv++;
        }
        if (live.current.metronome) {
          while (cursorClick < clicksPerCycle) {
            const at = cycleStart + cursorClick * beatQ * secsPerBeat;
            if (at > until) break;
            const osc = click(ctx.rawContext as unknown as AudioContext, at, cursorClick % beatsPerBar === 0);
            if (osc) clicks.push(osc);
            cursorClick++;
          }
        } else {
          cursorClick = clicksPerCycle;
        }

        const cycleDone = cursorEv >= picked.length
          && cursorClick >= clicksPerCycle;
        if (!cycleDone) break;
        if (!live.current.loop) {
          // Everything is queued; retire once the last of it has sounded.
          timers.push(window.setTimeout(
            () => { if (!cancelled) stop(); },
            Math.max(60, (cycleStart + cycleSecs - ctx.currentTime + 0.25) * 1000)));
          return;
        }
        if (cycleStart + cycleSecs > until) break;   // next cycle not due yet
        cycleStart += cycleSecs;
        clockRef.current = { startAt: t0, secsPerBeat, span };
        cursorEv = 0; cursorClick = 0;
      }

      if (!cancelled) timers.push(window.setTimeout(pump, REFILL));
    };

    if (countIn) {
      for (let b = 0; b < beatsPerBar; b++) {
        const osc = click(ctx.rawContext as unknown as AudioContext,
                          t0 - lead + b * beatQ * secsPerBeat, b === 0);
        if (osc) clicks.push(osc);
      }
    }
    pump();

    clockRef.current = { startAt: t0, secsPerBeat, span };
    setState((s) => ({ ...s, playing: true, loading: false }));

    // The highlight is a pure function of the audio clock, evaluated per frame.
    const tick = () => {
      rafRef.current = requestAnimationFrame(tick);
      const clock = clockRef.current;
      if (!clock) return;
      const elapsed = Tone.getContext().currentTime - clock.startAt;
      if (elapsed < 0) {
        beatRef.current = null;                    // count-in: nothing to point at yet
        setState((s) => (s.playingIndex === null ? s : { ...s, playingIndex: null, beat: 0 }));
        return;
      }
      const beats = live.current.loop
        ? (elapsed / clock.secsPerBeat) % clock.span
        : elapsed / clock.secsPerBeat;
      const cursor = origin + beats;
      let found: number | null = null;
      for (const ev of picked) {
        // The held length, so the highlight stays on the head of a tie for as
        // long as the sound does.
        if (cursor >= ev.n.start - 1e-6 && cursor < ev.n.start + ev.dur - 1e-6) {
          found = ev.i;
          break;
        }
      }
      beatRef.current = beats;
      // State changes only when the sounding note changes — roughly twice a
      // second rather than sixty times.
      setState((s) => (s.playingIndex === found ? s : { ...s, playingIndex: found, beat: beats }));
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [countIn, notes, score, stop]);

  return { ...state, play, stop, beatRef };
}

/** A short click for the count-in and the metronome, on its own gain stage.
 *  Returns the oscillator so the caller can silence it on Stop. */
function click(ctx: AudioContext, at: number, accent: boolean): OscillatorNode | null {
  if (!ctx || at < ctx.currentTime - 0.05) return null;
  try {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "square";
    osc.frequency.value = accent ? 1600 : 1100;
    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.exponentialRampToValueAtTime(accent ? 0.09 : 0.05, at + 0.004);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.05);
    osc.connect(gain).connect(ctx.destination);
    osc.start(at);
    osc.stop(at + 0.06);
    return osc;
  } catch {
    /* a missing click must never stop the music */
    return null;
  }
}
